/**
 * Pure 6×7 month-grid builder for the calendar popover. Returns 42
 * cells in row-major order, starting from the Sunday of the week
 * containing the first-of-month and continuing through the next-month
 * fill. `inMonth` distinguishes the "current month" days from the
 * leading/trailing fillers so the UI can mute the latter.
 *
 * Months are 0-indexed (`m: 0` = January) to mirror the Date API.
 */
export interface CalendarCell {
  /** Year of this cell. */
  y: number;
  /** Month of this cell (0-indexed). */
  m: number;
  /** Day-of-month of this cell. */
  d: number;
  /** Day-of-week 0=Sun..6=Sat. */
  dow: number;
  /** False for leading/trailing fillers from neighbouring months. */
  inMonth: boolean;
}

export function buildCalendarGrid(
  year: number,
  month: number,
): CalendarCell[] {
  const first = new Date(year, month, 1);
  const startDow = first.getDay();
  const cells: CalendarCell[] = [];
  for (let i = 0; i < 42; i++) {
    const offset = i - startDow;
    // Date arithmetic auto-rolls 0 / -1 / 32 / 33 into prev / next month.
    const dt = new Date(year, month, 1 + offset);
    cells.push({
      y: dt.getFullYear(),
      m: dt.getMonth(),
      d: dt.getDate(),
      dow: dt.getDay(),
      inMonth: dt.getMonth() === month && dt.getFullYear() === year,
    });
  }
  return cells;
}

/** Format a Y/M/D triple as the canonical `YYYY-MM-DD` string. */
export function fmtIsoDate(y: number, m: number, d: number): string {
  return `${String(y).padStart(4, '0')}-${String(m + 1).padStart(2, '0')}-${String(
    d,
  ).padStart(2, '0')}`;
}

/** Parse `YYYY-MM-DD` back to a Y/M/D triple, or null if malformed. */
export function parseIsoDate(
  iso: string,
): { y: number; m: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]) - 1, d: Number(m[3]) };
}
